<template>
  <div class="grade-level-summary">
    <h2 class="text-h6 font-weight-bold text-secondary mb-4">
      Summary of Alignment and Usability
    </h2>
    <div>
      <v-row no-gutters>
        <v-col
          v-for="grade in grades"
          :key="grade.name"
          cols="12"
          md="4"
          :class="{ 'pr-2': !isLastGrade(grade) }"
        >
          <v-card class="pa-2">
            <v-row align="center" class="pt-2">
              <v-col cols="6">
                <h4 class="font-weight-bold text-secondary">
                  {{ grade.name }}
                </h4>
              </v-col>
              <v-col cols="6" align="right">
                <a
                  class="text-primary font-weight-bold text-uppercase"
                  :href="grade.reportUrl || '#'"
                >
                  View Report
                </a>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-card
                  v-for="gateway in grade.gateways"
                  :key="gateway.name"
                  :class="[
                    getGatewayCardClass(gateway.status),
                    'p-0',
                    { 'mb-3': !isLastGateway(gateway) },
                  ]"
                >
                  <v-row no-gutters class="px-3 pt-3 pb-2">
                    <v-col cols="8">
                      <div class="mb-1">
                        <h5 class="font-weight-bold mb-4">
                          {{ gateway.name }}
                        </h5>
                        <h5 class="font-weight-regular">
                          {{ gateway.description }}
                        </h5>
                      </div>
                    </v-col>
                    <v-col cols="4" class="align-self-end text-right">
                      <h1>{{ gateway.score }}</h1>
                    </v-col>
                  </v-row>
                  <div class="card-footer">
                    <span class="card-footer-text">{{ gateway.status }}</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
  interface Gateway {
    name: string;
    description: string;
    score: string;
    status: string;
  }

  interface Grade {
    name: string;
    reportUrl?: string;
    gateways: Gateway[];
  }

  interface Props {
    grades: Grade[];
  }

  const props = defineProps<Props>();

  function getGatewayCardClass(status: string): string {
    const statusMap: Record<string, string> = {
      'Meets Expectations': 'green-card',
      'Partially Meets Expectations': 'orange-card',
      'Does not Meet Expectations': 'red-card',
      'Not Eligible': 'grey-card',
    };
    return statusMap[status] || 'grey-card';
  }

  function isLastGrade(grade: Grade): boolean {
    return props.grades.indexOf(grade) === props.grades.length - 1;
  }

  function isLastGateway(gateway: Gateway): boolean {
    return (
      gateway === props.grades[0].gateways[props.grades[0].gateways.length - 1]
    );
  }
</script>

<style lang="scss" scoped>
  @use '../../styles/abstracts/variables' as *;
  @use '../../styles/abstracts/mixins' as *;

  .grade-level-summary {
    h4 {
      font-size: 16px;
    }

    .v-card {
      border: 1px solid rgba(var(--v-theme-border-light), 1);
    }
  }
</style>
